import React from "react";
import { Helmet } from "react-helmet";

// Define OpenGraph structure based on Strapi schema
interface OpenGraph {
  "og:title"?: string;
  "og:description"?: string;
  "og:image"?: {
    url?: string;
    formats?: {
      small?: { url?: string };
      medium?: { url?: string };
      thumbnail?: { url?: string };
    };
  };
  "og:url"?: string;
  "og:type"?: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  schema?: object;
  openGraph?: OpenGraph; // OpenGraph data from Strapi
  seoData?: any; // Full SEO data object
  children?: React.ReactNode;
  author?: string; // Add author prop
  publisher?: string; // Add publisher prop
}

const SITE_NAME = "Evefant"; // You can make this configurable
const DEFAULT_DESCRIPTION =
  "Encuentra todo para tu fiesta y nosotros te lo llevamos. Desde decoración con globos, hasta los bocaditos. Delivery en todo Lima."; // You can make this configurable
const DEFAULT_IMAGE = "%REACT_APP_IMAGE_FOLDER%/isotipo_azul_9c9ca23b51.webp"; // Default OG image
const DEFAULT_AUTHOR = "Evefant"; // Default author
const DEFAULT_PUBLISHER = "Evefant"; // Default publisher

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  type,
  schema,
  openGraph,
  seoData,
  children,
  author,
  publisher,
}) => {
  // Prefer backend SEO data if available
  const backendSEO = seoData || {};
  const og = openGraph || backendSEO.openGraph || {};

  // Prefer backend values, fallback to props, then defaults
  const ogTitle = og["og:title"] || backendSEO.title || title || SITE_NAME;
  const ogDescription =
    og["og:description"] ||
    backendSEO.description ||
    description ||
    DEFAULT_DESCRIPTION;
  const ogImage =
    og["og:image"]?.url || backendSEO.image || image || DEFAULT_IMAGE;
  const ogUrl =
    og["og:url"] ||
    backendSEO.url ||
    (typeof window !== "undefined" ? window.location.href : "");
  const ogType = og["og:type"] || backendSEO.type || type || "website";
  const metaAuthor = backendSEO.author || author || DEFAULT_AUTHOR;
  const metaPublisher = backendSEO.publisher || publisher || DEFAULT_PUBLISHER;

  // Ensure absolute image URL
  const absoluteImageUrl =
    ogImage && ogImage.startsWith("http")
      ? ogImage
      : typeof window !== "undefined"
      ? `${window.location.origin}${
          ogImage.startsWith("/") ? "" : "/"
        }${ogImage}`
      : ogImage;

  // Structured data
  const structuredData = backendSEO.structuredData || schema;

  return (
    <Helmet htmlAttributes={{ lang: "es" }}>
      {/* Basic tags */}
      <title>{ogTitle ? `${ogTitle} | ${SITE_NAME}` : SITE_NAME}</title>
      <meta name="description" content={ogDescription} />
      <meta name="author" content={metaAuthor} />
      <meta name="publisher" content={metaPublisher} />

      {/* Canonical URL */}
      <link rel="canonical" href={ogUrl} />

      {/* Robots tag (allow indexing by default) */}
      <meta name="robots" content="index,follow" />

      {/* Favicon (optional, but recommended for SEO/UX) */}
      <link rel="icon" href="/favicon.ico" />

      {/* OpenGraph tags */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_PE" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      {/* Optionally add twitter:site and twitter:creator if you have them */}
      {/* <meta name="twitter:site" content="@yourbrand" /> */}
      {/* <meta name="twitter:creator" content="@author" /> */}

      {/* Preload main image for CLS optimization */}
      {absoluteImageUrl && (
        <link rel="preload" as="image" href={absoluteImageUrl} />
      )}

      {/* Mobile tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#000000" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Language (alternate) */}
      <link rel="alternate" hrefLang="es" href={ogUrl} />

      {/* Schema.org JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional tags */}
      {children}
    </Helmet>
  );
};

export default SEO;
